import {Authenticator, useTheme, View} from '@aws-amplify/ui-react'

import * as NarusMaterial from '../shared/NarusMaterial'
import {Version} from '../shared/version'
import {SignInFooter} from './SignInFooter'
import {ProgressiveAuthProvider, useProgressiveAuth} from './progressive-auth-context'
import {waitForStableSession} from '../../lib/token-utils'

interface AmplifyAuthenticatorProps {
    children: (props: any) => JSX.Element
    hideSignUp?: boolean
    loginMechanisms?: ('email' | 'username' | 'phone_number')[]
    services?: any
    formFields?: any
}

/**
 * AWS Amplify Authenticator with integrated passkey support
 * Uses proper Amplify slots instead of DOM polling
 */
export function AmplifyAuthenticator({
                                         children,
                                         hideSignUp = true,
                                         loginMechanisms = ['email'],
                                         services,
                                         formFields
                                     }: AmplifyAuthenticatorProps): JSX.Element {
    return (
        <ProgressiveAuthProvider>
            <AmplifyAuthenticatorInner
                hideSignUp={hideSignUp}
                loginMechanisms={loginMechanisms}
                services={services}
                formFields={formFields}
            >
                {children}
            </AmplifyAuthenticatorInner>
        </ProgressiveAuthProvider>
    )
}

function AmplifyAuthenticatorInner({
                                       children,
                                       hideSignUp = true,
                                       loginMechanisms = ['email'],
                                       services,
                                       formFields
                                   }: AmplifyAuthenticatorProps): JSX.Element {
    const progressiveAuth = useProgressiveAuth()

    return (
        <Authenticator
            hideSignUp={hideSignUp}
            loginMechanisms={loginMechanisms}
            components={{
                Header() {
                    const {tokens} = useTheme()

                    return (
                        <View
                            padding={tokens.space.medium}
                            backgroundColor='white'
                            className='rounded-t-2xl flex flex-col items-center'
                        >
                            <NarusMaterial.Svg
                                src='logo'
                                element='div'
                                title='Compassion - A Lucent Health Solution'
                                style={{width: '240px'}}
                                className='mt-12'
                            />
                        </View>
                    )
                },
                Footer() {
                    return (
                        <Version
                            className='text-center'
                        />
                    )
                },
                SignIn: {
                    Footer: SignInFooter
                }
            }}
            services={{
                ...services,
                async handleSignIn(formData: any) {
                    const cleanedUsername = formData.username.toLowerCase().trim().replace(/\s+/g, '')
                    const cleanedPassword = formData.password.trim()

                    const {signIn, confirmSignIn} = await import('aws-amplify/auth')

                    // If we already initiated USER_AUTH flow to discover challenges, continue it
                    if (progressiveAuth.isChallengeReady) {
                        try {
                            let result

                            if (progressiveAuth.nextStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
                                result = await confirmSignIn({
                                    challengeResponse: 'PASSWORD'
                                })
                            }

                            if (!result || result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
                                result = await confirmSignIn({
                                    challengeResponse: cleanedPassword
                                })
                            }

                            progressiveAuth.resetChallenges()
                            await waitForStableSession()
                            return result
                        } catch (err) {
                            // Fall back to a regular password sign-in if the continuation fails
                        }
                    }

                    const res = await signIn({username: cleanedUsername, password: cleanedPassword})
                    await waitForStableSession()
                    return res
                }
            }}
            formFields={formFields}
        >
            {children}
        </Authenticator>
    )
}
